import React, { useState, useEffect, useCallback } from "react";

interface ManagedTextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> {
    /** Unique identifier for the underlying HTML textarea element (useful for labels) */
    id: string;
    /** The current committed value from the parent's state hook */
    value: string;
    /** The function from the parent's state hook to update the latest value (e.g., the 'update' function) */
    updateValue: (newValueOrFn: React.SetStateAction<string>) => void;
}

/**
 * ManagedTextArea Component
 *
 * Integrates with a parent's state management hook (like useDirtyState)
 * via individual `value` and `updateValue` props.
 *
 * Assumes the state management hook handles update modes (immediate,
 * debounced, manual).
 */
const ManagedTextArea: React.FC<ManagedTextAreaProps> = ({
    id,
    value: committedValue, // Rename prop locally to avoid conflict with internal state
    updateValue, // Callback to update parent use of text area value
    ...restProps
}) => {

    const [displayValue, setDisplayValue] = useState<string>(committedValue);

    // ---=== Event Handlers ===--- //

    const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        // console.log("ManagedTextArea handleChange fired");
        const newValue = event.target.value;
        setDisplayValue(newValue);
        updateValue(newValue);
    }, [updateValue]);

    // ---=== Effects ===--- //

    // Sync internal display state if the committed value from the parent changes externally.
    useEffect(() => {
        // console.log(`useEffect: display[${displayValue}] committed[${committedValue}]`);
        if (displayValue !== committedValue) {
            setDisplayValue(committedValue);
        }
    }, [committedValue]);

    // ---=== Render ===--- //

    return (
        <textarea
            id={id}
            value={displayValue}
            onChange={handleChange}
            {...restProps}
        />
    );
};

export default ManagedTextArea;
